import { ReactNode } from 'react'
import cn from 'classnames'
import { DashboardContainer } from '@forwardflow/components/Dashboard'
import {
   FlowIcon,
   TFlowIcon,
} from '@forwardflow/features/campaign-flow/components/ui/FlowIcon'
import { useFlowBase } from '@forwardflow/features/campaign-flow/hooks/useFlowBase'

const NAV: TFlowIcon[] = ['home', 'chat', 'phone']

/**
 * The page frame. Signed in: the real dashboard (header, side nav). Preview:
 * the same layout drawn statically, so the flow can be opened without an
 * account and without any API call. The content area has the same size and
 * scroll behaviour in both.
 */
export const FlowShell = ({
   children,
   className = '',
}: {
   children: ReactNode
   className?: string
}) => {
   const { preview } = useFlowBase()
   if (!preview)
      return (
         <DashboardContainer className={className}>
            {children}
         </DashboardContainer>
      )
   return (
      <div
         className="min-h-screen flex flex-col"
         style={{
            background: 'linear-gradient(112deg,#18191d 0%,#222429 108.67%)',
         }}
         data-testid="cf-preview-shell"
      >
         <div
            className="pointer-events-none fixed inset-0"
            style={{
               background:
                  'radial-gradient(ellipse at 10% 20%,rgba(238,86,169,.15) -20%,transparent 50%),radial-gradient(ellipse at 90% 90%,rgba(144,97,218,.3) 0%,transparent 60%)',
            }}
         />
         <header className="relative h-20 shrink-0 flex items-center justify-between px-[30px] lg:pl-11 lg:pr-10">
            <div className="w-[52px] h-14 rounded-xl bg-white/[0.08] flex items-center justify-center">
               <img
                  src="/images/ff-sm-logo.png"
                  alt="Forward Flow"
                  className="w-[30px] h-[30px] object-contain"
               />
            </div>
            <span className="hidden sm:inline text-xs font-semibold tracking-[.08em] uppercase text-[#DAC2FF] bg-[#9061DA]/20 rounded-full px-3 py-1.5">
               Preview · demo data, nothing is saved
            </span>
            <div className="w-14 h-14 rounded-full bg-white/[0.12] flex items-center justify-center text-lg font-semibold text-white">
               S
            </div>
         </header>
         <div className="relative flex flex-1">
            <nav className="hidden lg:flex w-[130px] shrink-0 flex-col items-start pl-11 pt-[265px] gap-5">
               {NAV.slice(0, 1).map((i) => (
                  <span
                     key={i}
                     className="w-[42px] h-[42px] rounded-[10px] flex items-center justify-center text-white/55"
                  >
                     <FlowIcon name={i} size={20} />
                  </span>
               ))}
               <span className="w-[42px] h-[42px] rounded-[10px] flex items-center justify-center bg-white/10 text-[#DAC2FF]">
                  <FlowIcon name="zap" size={20} />
               </span>
               {NAV.slice(1).map((i) => (
                  <span
                     key={i}
                     className="w-[42px] h-[42px] rounded-[10px] flex items-center justify-center text-white/55"
                  >
                     <FlowIcon name={i} size={20} />
                  </span>
               ))}
            </nav>
            <section
               className={cn(
                  'flex-1 min-w-0 pb-8 pt-6 lg:pt-0 px-[30px] lg:pl-0 lg:pr-11 h-[calc(100vh-80px)] overflow-y-auto',
                  className
               )}
            >
               {children}
            </section>
         </div>
      </div>
   )
}
